import { createApi } from '@reduxjs/toolkit/query/react'
import {
  getBaseQuery,
  getBaseUrl,
  REQUEST_TIMEOUT_MS,
} from '@/apis/api.util.ts'
import {
  CheckIdeaExistRequest,
  CheckIdeaExistResponse,
  ClearIdeaActionAlertRequest,
  ClearIdeaAlertsRequest,
  IdeaCheckFillStrategyRequest,
  IdeaCheckFillStrategyResponse,
  IdeaCreateRequest,
  IdeaDailyPnlResponse,
  IdeaDetailResponse,
  IdeaDetailResponseRaw,
  IdeaHistoryResponse,
  IdeaLiquidateRequest,
  IdeaListRequest,
  IdeaListResponse,
  IdeaModifyResponse,
  IdeaOrdersResponse,
  IdeaToggleKeepFillingRequest,
  IdeaToggleKeepFillingResponse,
  IdeaUpdateRequest,
} from '@/apis/idea/idea-api.def.ts'
import type { SSRMGridFetchResult } from '@/common/ag-grid/grid.types.ts'
import {
  toUnifiedIdeaRow,
  toUnifiedOrderRows,
  type UnifiedIdeaRow,
} from '@/pages/idea-list/components/idea-list-table/idea-list-ssrm-adapter.ts'
import { orderBy } from 'lodash'

const interfaceName = 'idea'

export const ideaApi = createApi({
  reducerPath: 'ideaApi',
  baseQuery: getBaseQuery(getBaseUrl(interfaceName), REQUEST_TIMEOUT_MS),
  tagTypes: ['idea-list'],
  endpoints: (build) => ({
    listIdeas: build.query<SSRMGridFetchResult, IdeaListRequest>({
      query: (params) => ({
        url: '/list',
        method: 'GET',
        params: params,
      }),
      transformResponse: (response: IdeaListResponse): SSRMGridFetchResult => ({
        rows: response.data.ideas.map(toUnifiedIdeaRow),
        totalCount: response.data.total,
      }),
      providesTags: ['idea-list'],
    }),
    createIdea: build.mutation<IdeaModifyResponse, IdeaCreateRequest>({
      query: (payload) => ({
        url: '/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['idea-list'],
    }),
    getIdeaDetail: build.query<IdeaDetailResponse, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'GET',
      }),
      transformResponse: (
        response: IdeaDetailResponseRaw
      ): IdeaDetailResponse => ({
        ...response,
        data: {
          ...response.data,
          target: response.data.targetPrice,
          stop: response.data.stopPrice,
          auto_fill: response.data.auto_fill ?? false,
        },
      }),
      providesTags: ['idea-list'],
    }),
    checkIdeaExist: build.query<CheckIdeaExistResponse, CheckIdeaExistRequest>({
      query: (payload) => ({
        url: '/check-exist',
        method: 'POST',
        body: payload,
      }),
    }),
    getIdeaHistory: build.query<IdeaHistoryResponse, string>({
      query: (id) => ({
        url: `/${id}/history`,
        method: 'GET',
      }),
      providesTags: ['idea-list'],
    }),
    getDailyIdeaPnl: build.query<IdeaDailyPnlResponse, string>({
      query: (id) => ({
        url: `/${id}/daily-pnl`,
        method: 'GET',
      }),
      providesTags: ['idea-list'],
    }),
    getIdeaOrders: build.query<UnifiedIdeaRow[], string>({
      query: (id) => ({
        url: `/${id}/orders`,
        method: 'GET',
      }),
      transformResponse: (response: IdeaOrdersResponse): UnifiedIdeaRow[] =>
        toUnifiedOrderRows(
          orderBy(response.data.orders, [(o) => o.createdAt], ['desc'])
        ),
      providesTags: ['idea-list'],
    }),
    updateIdea: build.mutation<IdeaModifyResponse, IdeaUpdateRequest>({
      query: (payload) => ({
        url: `/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['idea-list']),
    }),
    liquidateIdea: build.mutation<IdeaModifyResponse, IdeaLiquidateRequest>({
      query: (payload) => ({
        url: `/${payload.id}/liquidate`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['idea-list'],
    }),
    checkFillStrategy: build.query<
      IdeaCheckFillStrategyResponse,
      IdeaCheckFillStrategyRequest
    >({
      query: (payload) => ({
        url: `/${payload.id}/check-fill-strategy`,
        method: 'POST',
        body: payload,
      }),
    }),
    clearIdeaAlerts: build.query<void, ClearIdeaAlertsRequest>({
      query: (payload) => ({
        url: `/${payload.id}/alert/off`,
        method: 'POST',
        body: payload,
      }),
    }),
    clearIdeaActionAlert: build.query<void, ClearIdeaActionAlertRequest>({
      query: (payload) => ({
        url: `/idea-action/${payload.actionId}/alert/off`,
        method: 'POST',
        body: payload,
      }),
    }),
    toggleKeepFilling: build.mutation<
      IdeaToggleKeepFillingResponse,
      IdeaToggleKeepFillingRequest
    >({
      query: (payload) => ({
        url: `/${payload.id}/toggle-auto-fill`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['idea-list'],
    }),
  }),
})

export const {
  useCreateIdeaMutation,
  useGetIdeaDetailQuery,
  useCheckIdeaExistQuery,
  useGetIdeaHistoryQuery,
  useGetDailyIdeaPnlQuery,
  useUpdateIdeaMutation,
  useLiquidateIdeaMutation,
  useCheckFillStrategyQuery,
  useLazyClearIdeaAlertsQuery,
  useLazyClearIdeaActionAlertQuery,
  useToggleKeepFillingMutation,
  useLazyListIdeasQuery,
  useGetIdeaOrdersQuery,
  useLazyGetIdeaOrdersQuery,
} = ideaApi
